import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Timer, ChevronLeft, ChevronRight, Flag, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

function pad(n) { return String(n).padStart(2, "0"); }

function safeJSON(raw) {
  try { return JSON.parse(raw); } catch { return null; }
}

function fmtCountdown(ms) {
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export default function MockExamRunner() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [idx, setIdx] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const expiredHandled = useRef(false);
  const bootedRef = useRef(false); // prevents progress-save effect from firing pre-hydration

  // Bootstrap — Phase 46: ALWAYS hit the API so we get authoritative
  // current_idx, flagged_ids, and any answers saved from another device /
  // previous crashed session. sessionStorage cache is no longer trusted for
  // idx/flagged because it dies on browser restart, but we keep the question
  // payload cache to avoid a slow re-fetch on quick reload.
  useEffect(() => {
    let cancelled = false;
    const cachedRaw = sessionStorage.getItem(`mock_exam_${sessionId}`);
    const cached = cachedRaw ? safeJSON(cachedRaw) : null;
    if (cached?.questions?.length) {
      setQuestions(cached.questions);
      setExpiresAt(cached.expires_at);
      setAnswers(cached.answers || {});
    }
    api.get(`/student/mock-exam/${sessionId}`).then((r) => {
      if (cancelled) return;
      if (r.data.status === "completed") {
        nav(`/student/mock-exam/${sessionId}/results`, { replace: true });
        return;
      }
      const serverIdx = Number.isInteger(r.data.current_idx) ? r.data.current_idx : 0;
      const serverFlagged = Array.isArray(r.data.flagged_ids) ? r.data.flagged_ids : [];
      setQuestions(r.data.questions || []);
      setExpiresAt(r.data.expires_at);
      setAnswers(r.data.answers || {});
      setIdx(serverIdx);
      setFlagged(Object.fromEntries(serverFlagged.map((id) => [id, true])));
      bootedRef.current = true;
      // Friendly resume toast only if there's meaningful prior state
      const hadProgress = serverIdx > 0 || Object.keys(r.data.answers || {}).length > 0;
      if (hadProgress) {
        toast.success(`Welcome back — resuming at question ${serverIdx + 1}`, {
          duration: 4500,
          description: "Your progress, timer and flagged questions were saved.",
        });
      }
    }).catch(() => {
      if (cancelled) return;
      toast.error("Could not load mock exam");
      nav("/student/mock-exam", { replace: true });
    });
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  // 1-second clock for the countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Phase 46 — Smart Resume: debounce-save current_idx + flagged_ids to server
  // every 500ms after a change. Fire-and-forget; on failure we silently retry
  // on the next change. Skips before bootstrap finishes (bootedRef guard).
  useEffect(() => {
    if (!bootedRef.current) return;
    const flaggedIds = Object.keys(flagged).filter((id) => flagged[id]);
    const t = setTimeout(() => {
      api.patch(`/student/mock-exam/${sessionId}/progress`, {
        current_idx: idx,
        flagged_ids: flaggedIds,
      }).catch(() => { /* silent retry on next change */ });
    }, 500);
    return () => clearTimeout(t);
  }, [idx, flagged, sessionId]);

  // Phase 46 — Final progress flush on tab/browser close via sendBeacon. This
  // is the ONLY way to guarantee a fetch completes during page unload (regular
  // fetch is canceled). Works even if the device dies a beat later.
  useEffect(() => {
    const flush = () => {
      if (!bootedRef.current) return;
      const url = `${API}/student/mock-exam/${sessionId}/progress`;
      const flaggedIds = Object.keys(flagged).filter((id) => flagged[id]);
      const payload = JSON.stringify({ current_idx: idx, flagged_ids: flaggedIds });
      const tok = localStorage.getItem("token");
      // navigator.sendBeacon cannot set Authorization headers (only Content-Type),
      // so fall back to a keepalive fetch which CAN. Both survive page unload.
      if (tok && typeof fetch === "function") {
        try {
          fetch(url, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: payload,
            keepalive: true,
          });
        } catch { /* best-effort only */ }
      }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [sessionId, idx, flagged]);

  const remainingMs = expiresAt ? new Date(expiresAt).getTime() - now : 0;
  const lowTime = remainingMs > 0 && remainingMs < 5 * 60 * 1000;

  const handleSubmit = useCallback(async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/student/mock-exam/${sessionId}/complete`);
      sessionStorage.removeItem(`mock_exam_${sessionId}`);
      if (auto) toast.warning("Time's up — submitting your exam");
      nav(`/student/mock-exam/${sessionId}/results`, { replace: true });
    } catch {
      toast.error("Could not submit — please try again");
      setSubmitting(false);
    }
  }, [sessionId, submitting, nav]);

  // Auto-submit when timer hits zero
  useEffect(() => {
    if (!expiresAt) return;
    if (remainingMs <= 0 && !expiredHandled.current) {
      expiredHandled.current = true;
      handleSubmit(true);
    }
  }, [remainingMs, expiresAt, handleSubmit]);

  const pickAnswer = async (label) => {
    const q = questions[idx];
    if (!q) return;
    setAnswers((prev) => ({ ...prev, [q.id]: label }));
    // Fire-and-forget — server is idempotent, last-write-wins
    api.post("/student/mock-exam/answer", {
      session_id: sessionId,
      question_id: q.id,
      selected_answer: label,
    }).catch(() => {
      toast.error("Couldn't save that answer — try again");
    });
  };

  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = () => setIdx((i) => Math.min(questions.length - 1, i + 1));

  if (!questions.length) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading exam…
      </div>
    );
  }

  const current = questions[idx];
  const answeredCount = Object.keys(answers).length;
  const progressPct = (answeredCount / questions.length) * 100;
  const selected = answers[current.id];

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="mock-exam-runner">
      {/* Sticky timer + progress header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-bold uppercase tracking-wider text-[10px]">
              Mock Exam
            </Badge>
            <div className="text-sm text-slate-600 tabular-nums">
              <strong className="text-slate-900">Q {idx + 1}</strong> of {questions.length}
              <span className="text-slate-400 mx-2">·</span>
              <strong className="text-slate-900">{answeredCount}</strong> answered
            </div>
          </div>
          <div
            className={`flex items-center gap-2 font-mono font-bold text-lg tabular-nums px-3 py-1.5 rounded-md ${
              lowTime ? "bg-rose-100 text-rose-700 animate-pulse" : "bg-slate-100 text-slate-800"
            }`}
            data-testid="mock-exam-timer"
          >
            <Timer className="w-4 h-4" /> {fmtCountdown(remainingMs)}
          </div>
        </div>
        <Progress value={progressPct} className="h-1 mt-2" />
      </div>

      <Card className="p-6 sm:p-8 border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Question {idx + 1}
          </div>
          <button
            type="button"
            onClick={() => setFlagged((f) => ({ ...f, [current.id]: !f[current.id] }))}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md border transition-colors ${
              flagged[current.id]
                ? "bg-amber-100 text-amber-800 border-amber-300"
                : "text-slate-500 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid="mock-exam-flag-btn"
          >
            <Flag className="w-3.5 h-3.5" /> {flagged[current.id] ? "Flagged" : "Flag"}
          </button>
        </div>
        <p className="text-xl leading-relaxed text-slate-900 font-medium mt-3">
          {current.question_text}
        </p>

        <div className="mt-6 space-y-2.5">
          {current.display_choices.map((c) => {
            const active = selected === c.display;
            return (
              <button
                key={c.display}
                type="button"
                onClick={() => pickAnswer(c.display)}
                className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
                  active
                    ? "border-amber-500 bg-amber-50 text-slate-900"
                    : "border-slate-200 bg-white hover:bg-slate-50 text-slate-800"
                }`}
                data-testid={`mock-exam-choice-${c.display}`}
              >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 font-bold text-sm ${
                  active ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"
                }`}>{c.display}</span>
                {c.text}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
          <Button variant="outline" onClick={goPrev} disabled={idx === 0} data-testid="mock-exam-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1.5" /> Previous
          </Button>
          {idx < questions.length - 1 ? (
            <Button onClick={goNext} className="bg-slate-800 hover:bg-slate-900" data-testid="mock-exam-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={() => setConfirmOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="mock-exam-finish-btn"
            >
              Review &amp; Submit <ChevronRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </div>
      </Card>

      {/* Question palette — quick-jump grid */}
      <Card className="p-4 border-slate-200" data-testid="mock-exam-palette">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
          Question Navigator
        </div>
        <div className="grid grid-cols-10 sm:grid-cols-15 gap-1.5">
          {questions.map((q, i) => {
            const answered = !!answers[q.id];
            const isFlagged = !!flagged[q.id];
            const isCurrent = i === idx;
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => setIdx(i)}
                className={`relative h-8 rounded text-[11px] font-bold tabular-nums border transition-colors ${
                  isCurrent
                    ? "border-amber-500 bg-amber-500 text-white"
                    : answered
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`mock-exam-palette-${i}`}
              >
                {i + 1}
                {isFlagged && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500" />
                )}
              </button>
            );
          })}
        </div>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Submit your exam?
            </DialogTitle>
            <DialogDescription>
              You&apos;ve answered <strong>{answeredCount}</strong> of <strong>{questions.length}</strong> questions.
              {answeredCount < questions.length && (
                <span className="block mt-2 text-rose-700">
                  Unanswered questions will count as wrong. You can&apos;t change answers after submitting.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Keep working
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="mock-exam-confirm-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit final answers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
